import { Link } from "react-router-dom";
import Seo, { breadcrumbs, faqSchema, webPageSchema, serviceSchema, articleSchema } from "../components/Seo";
import LeadForm from "../components/LeadForm";
import CtaCallout from "../components/CtaCallout";
import LiveInventory from "../components/LiveInventory";
import { SectionHeader } from "../components/UI";
import AboutScottBlurb from "../components/AboutScottBlurb";
import EvCostCalculator from "../components/EvCostCalculator";
import ChargingStationMap from "../components/ChargingStationMap";
import { siteConfig } from "../config";

const FAQS = [
  { q: "How long does charging take?", a: "Level 1 (120V) is slow — good as a top-off. Level 2 (240V) at home typically fills overnight. DC fast charging can add a large amount of range in 20–40 minutes when conditions are right." },
  { q: "Can I charge at home?", a: "Yes — most EV owners install a Level 2 charger or use a 240V outlet. A dedicated 240V circuit gives the best at-home experience." },
  { q: "What is Level 1, Level 2, and DC fast charging?", a: "Level 1 uses a standard outlet, Level 2 uses 240V (much faster), and DC fast charging uses high-power public stations for rapid range adds." },
  { q: "Why does fast charging slow down after 80%?", a: "Lithium batteries slow charging at higher states to protect cells and longevity. Planning trips around 10%–80% is usually most efficient." },
  { q: "How much range do I really need?", a: "Most drivers need less daily range than they think. Match real commute and weekend trips to a vehicle's rated range with weather buffer in mind." },
  { q: "Are EVs good in Ohio winters?", a: "Modern EVs handle Ohio winters well. Range can drop in cold temps, but pre-conditioning while plugged in helps maintain efficiency and comfort." },
  { q: "Should I lease or buy an EV?", a: "Leasing can capture certain incentives and protects you against tech changes. Buying makes sense if you want long-term ownership. Scott can help you weigh both." },
];

export default function EvHelp() {
  return (
    <main id="main-content">
      <Seo
        title="EV Shopping Help in Central Ohio | Scott Sells Chevys"
        description="Plain-English EV help in Central Ohio — range, charging, home charging, public DC fast charging, ownership costs, and which Chevy EV may fit your life."
        canonicalPath="/ev-help"
        jsonLd={[
          breadcrumbs([{ name: "Home", path: "/" }, { name: "EV Help", path: "/ev-help" }]),
          faqSchema(FAQS),
          webPageSchema({ name: "EV Shopping Help", path: "/ev-help", description: "EV ownership, charging, and range education with Scott Hall." }),
          serviceSchema({
            name: "EV Shopping Help",
            serviceType: "Electric vehicle ownership education",
            path: "/ev-help",
            description: "Scott walks you through range, charging, home Level-2, and ownership cost so you can decide if an EV makes sense for your life.",
          }),
          articleSchema({
            headline: "EV Shopping Help in Central Ohio",
            path: "/ev-help",
            description: "A plain-English guide to EV range, charging, and ownership in Ohio.",
          }),
        ]}
      />
      <section className="ssc-hero">
        <div className="ssc-container py-10 md:py-16">
          <h1 className="ssc-display text-3xl md:text-5xl font-extrabold">EV Shopping Help in Central Ohio</h1>
          <p className="mt-3 text-[var(--ssc-silver)] max-w-2xl">Real answers about range, charging, ownership, and which EV may make sense for your life.</p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to="/new-vehicles/evs" className="ssc-btn ssc-btn-primary">Shop New EVs</Link>
            <Link to="/used-vehicles/evs" className="ssc-btn ssc-btn-outline">Shop Used EVs</Link>
          </div>
        </div>
      </section>

      <section className="ssc-section">
        <div className="ssc-container">
          <LiveInventory
            title="EVs Available Right Now"
            subtitle="Live electric vehicle inventory — Equinox EV, Blazer EV, Silverado EV, and more."
            params={{ body_style: "ev" }}
            limit={4}
            viewAllTo="/new-vehicles/evs"
            testId="ev-live-inventory"
          />
        </div>
      </section>

      <section className="ssc-section">
        <div className="ssc-container">
          <AboutScottBlurb
            heading="EV Questions, Plain Answers"
            text="EV shopping comes with different questions than gas vehicle shopping. Scott can help explain range, charging, home charging, public charging, fast charging, and how EV ownership fits real life in Central Ohio."
            testId="ev-about-scott"
          />
        </div>
      </section>

      <section className="ssc-section">
        <div className="ssc-container">
          <SectionHeader
            title="See Your Real EV Savings vs Gas"
            subtitle="Tweak the numbers — Central Ohio rates, your monthly miles, your driving mix. Get a real 5-year picture in seconds."
          />
          <EvCostCalculator />
        </div>
      </section>

      <section className="ssc-section bg-[var(--ssc-silver-2)]">
        <div className="ssc-container">
          <SectionHeader
            title="Charging Stations Near You"
            subtitle="See where the real public chargers are before you commit. Powered by the U.S. Dept. of Energy."
          />
          <ChargingStationMap />
        </div>
      </section>

      <section className="ssc-section">
        <div className="ssc-container grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            ["Charging Basics", "Plug in at home, top off at work, fast charge on road trips."],
            ["Range Basics", "Match daily and weekend driving to a vehicle's real-world range with weather buffer."],
            ["Home Charging", "A 240V Level 2 charger is the easiest at-home upgrade for daily EV life."],
            ["Public Charging", "Plan around DC fast chargers when traveling longer distances."],
            ["EV Tax Credit Basics", "Federal and state programs can affect both lease and buy choices — Scott can help you sort it."],
            ["EV vs Gas Ownership", "Charging, maintenance, and total cost of ownership differ. We compare them honestly."],
          ].map(([t, p]) => (
            <div key={t} className="ssc-card">
              <div className="font-extrabold text-[var(--ssc-navy)]">{t}</div>
              <p className="text-[var(--ssc-ink-2)] mt-1">{p}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="ssc-section bg-[var(--ssc-silver-2)]">
        <div className="ssc-container">
          <SectionHeader title="EV FAQ" />
          <div className="grid md:grid-cols-2 gap-4">
            {FAQS.map((f, i) => (
              <div key={i} className="ssc-card">
                <div className="font-extrabold text-[var(--ssc-navy)]">{f.q}</div>
                <p className="text-[var(--ssc-ink-2)] mt-2">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="ssc-section">
        <div className="ssc-container max-w-3xl">
          <SectionHeader title="Ask Scott About EVs" center />
          <LeadForm
            leadType="ev_help"
            leadSource="EV Help Page"
            testIdPrefix="ev-lead"
            fields={[
              { name: "name", label: "Name", required: true, autoComplete: "name" },
              { name: "phone", label: "Phone", inputMode: "tel", autoComplete: "tel" },
              { name: "email", label: "Email", type: "email", autoComplete: "email" },
              { name: "preferred_contact", label: "Preferred Contact", type: "select", options: ["Text", "Call", "Email"] },
              { name: "ev_question", label: "What do you want to know about EVs?", type: "textarea", full: true },
            ]}
            submitLabel="Send to Scott"
          />
          <CtaCallout
            testId="ev-cta-callout"
            heading="Want Scott to walk you through an EV?"
            sub="Text him with how many miles a day you drive and whether you have a garage — he'll reply with which EV fits."
            actions={[
              { key: "text", label: "Text Scott", icon: "text", href: siteConfig.scott.phoneSms },
              { key: "find", label: "Find an EV", icon: "find", to: "/find-me-a-vehicle" },
              { key: "payment", label: "Payment Help", icon: "payment", to: "/payment-help" },
              { key: "trade", label: "Value My Trade", icon: "trade", to: "/value-my-trade" },
            ]}
          />
        </div>
      </section>
    </main>
  );
}
